<template>
  <div :class="focusedOnboardingFlow ? '' : 'form__group--border py-16 pb-32'">
    <p class="form__label">Set your base hourly rate <span v-if="childCare">for 1 child</span></p>
    <p class="text-caption">You can update your rate at anytime</p>
    <PaymentRateNoteBox :childCare="childCare" :payRateFetchError="payRateFetchError" :payRange="payRange" />
    <fieldset>
      <legend class="sr-only">Set your base hourly rate <span v-if="childCare">for 1 child</span></legend>
      <div class="inline-flex">
        <poppins-text-field
          id="min"
          ref="minimumRate"
          v-model="min"
          labelName="$ Min"
          :aria-label="'Set your minimum rate'"
          name="minimum-rate"
          type="text"
          pattern="[0-9]*"
          inputmode="numeric"
          :status="formStatuses.min"
          :required="true"
          :limit-input-to-pattern="true"
          @blur="validateMinimumRate"
        />
        <p class="p-16">to</p>
        <poppins-text-field
          id="max"
          ref="maximumRate"
          v-model="max"
          labelName="$ Max"
          :aria-label="'Set your maximum rate'"
          name="maximum-rate"
          type="text"
          pattern="[0-9]*"
          inputmode="numeric"
          :status="formStatuses.max"
          :required="true"
          :limit-input-to-pattern="true"
          @blur="validateMaximumRate"
        />
      </div>
      <div v-if="hasErrors" class="mt-4" data-test-id="errors">
        <div v-for="key in formStatusesErrors" :key="key" class="flex relative">
          <HelperText :id="key" :text="helperText(0, key)" :status="0" />
        </div>
      </div>
    </fieldset>
    <div class="mt-20">
      <HelperText v-if="childCare" :text="helperTextMessage" />
      <HelperText v-else text="Average rates vary based on location and experience" />
    </div>
  </div>
</template>

<script lang="ts">
import currency from 'currency.js';
import helperStatus from '@/helpers/status';
import TextField from '@/components/textfield/TextField.vue';
import PaymentRateNoteBox from './PaymentRateNoteBox.vue';
import focusOnElement from '@/helpers/focusHelper';
import OnboardingQueries from '@/graphql/onboarding/queries';
import HelperText from '@/components/helper_text/HelperText.vue';

export default {
  name: 'PaymentRateFields',
  components: {
    PoppinsTextField: TextField,
    PaymentRateNoteBox,
    HelperText,
  },
  data(): any {
    return {
      formStatuses: {
        min: helperStatus.Neutral,
        max: helperStatus.Neutral,
      },
      careType: this.$store.state.sitterOnboarding.careType,
      geoPoint: {
        x: '',
        y: '',
      },
      averagePayRate: null,
      payRateFetchError: false,
    };
  },
  computed: {
    helperTextMessage() {
      return `Need more help? <a
          class="link--secondary primary-focus ml-2"
          href="https://www.sittercity.com/sitters/working-with-families/much-charge-babysitting-nannying"
          target="_blank"
          >Learn how to calculate your rate.</a
        >`;
    },
    min: {
      get() {
        return this.$store.getters.minPaymentRate;
      },
      set(newValue) {
        this.resetErrorState('min');
        this.resetErrorState('max');
        this.$store.dispatch('updatePaymentRate', { key: 'min', value: newValue });
      },
    },
    max: {
      get() {
        return this.$store.getters.maxPaymentRate;
      },
      set(newValue) {
        this.resetErrorState('max');
        this.$store.dispatch('updatePaymentRate', { key: 'max', value: newValue });
      },
    },
    yearsExperience: {
      get() {
        return this.$store.getters.yearsExperience;
      },
    },
    focusedOnboardingFlow() {
      return window.location.pathname.includes('/profile/steps');
    },
    childCare() {
      return this.careType !== 'housekeeping';
    },
    // backend expects an Int, TextField expects a String, so we have to convert
    minRateCents() {
      return currency(this.min).intValue;
    },
    maxRateCents() {
      return currency(this.max).intValue;
    },
    minimumRateIsValid(): boolean {
      return this.min && this.minRateCents >= 800;
    },
    maximumRateIsValid(): boolean {
      return this.max && this.maxRateCents <= 99900;
    },
    maximumRateIsMoreThanMinimum(): boolean {
      return this.maxRateCents > this.minRateCents;
    },
    payRange() {
      if (this.averagePayRate > 0) {
        const min = this.averagePayRate - 2;
        const max = this.averagePayRate + 2;
        return { min, max };
      }
      return { min: 20, max: 23 };
    },
    formStatusesErrors(): Array<string> {
      return Object.keys(this.formStatuses).filter((key) => this.formStatuses[key] === helperStatus.Error);
    },
    hasErrors(): boolean {
      return this.formStatusesErrors.length > 0;
    },
  },
  watch: {
    // On megaform, update average pay rate when years experience is updated
    yearsExperience() {
      if (this.childCare && !this.payRateFetchError) {
        this.fetchAveragePayRate();
      }
    },
  },
  mounted() {
    if (this.childCare) {
      this.fetchSitterGeopoint();
    }
  },
  methods: {
    triggerErrorState(fieldName) {
      this.formStatuses[fieldName] = helperStatus.Error;
    },
    resetErrorState(fieldName) {
      this.formStatuses[fieldName] = helperStatus.Neutral;
    },
    generateReadableRate(key) {
      return key === 'min' ? 'Minimum rate' : 'Maximum rate';
    },
    validate() {
      this.validateMinimumRate();
      this.validateMaximumRate();

      if (this.formStatuses.min && this.formStatuses.max) {
        return helperStatus.Neutral; // truthy
      }
      return helperStatus.Error; // falsy
    },
    validateMinimumRate() {
      if (!this.minimumRateIsValid) {
        this.triggerErrorState('min');
      }
      return this.formStatuses.min;
    },
    validateMaximumRate() {
      if (!this.maximumRateIsValid || !this.maximumRateIsMoreThanMinimum) {
        this.triggerErrorState('max');
      }
      return this.formStatuses.max;
    },
    helperText(status: helperStatus, type: string) {
      const readableType = this.generateReadableRate(type);

      if (status !== helperStatus.Error) return '';

      if (type === 'min' && this.min && !this.minimumRateIsValid) {
        return `${readableType} must be more than $7`;
      }
      if (type === 'max' && this.max && !this.maximumRateIsValid) {
        return `${readableType} must be less than $1,000`;
      }
      if (type === 'max' && this.min && this.max && !this.maximumRateIsMoreThanMinimum) {
        return 'Enter a maximum rate that is greater than the minimum rate';
      }
      return `${readableType} is required`;
    },
    focusElement() {
      let elementToFocus;

      if (!this.minimumRateIsValid) {
        elementToFocus = this.$refs.minimumRate;
      } else if (!this.maximumRateIsValid) {
        elementToFocus = this.$refs.maximumRate;
      } else {
        elementToFocus = this.$refs.minimumRate;
      }
      focusOnElement(elementToFocus.$el.querySelector('input'));
    },
    fetchSitterGeopoint() {
      this.$apollo
        .query({
          query: OnboardingQueries.FETCH_SITTER_LOCATION,
        })
        .then(({ data }) => {
          this.geoPoint.x = parseFloat(data.viewer.location.geoPoint.x);
          this.geoPoint.y = parseFloat(data.viewer.location.geoPoint.y);
          this.fetchAveragePayRate();
        })
        .catch((error) => {
          this.payRateFetchError = true;
          console.log(error.message); // eslint-disable-line no-console
        });
    },
    fetchAveragePayRate() {
      this.$apollo
        .query({
          query: OnboardingQueries.FETCH_AVERAGE_SITTER_PAY_RATE,
          variables: {
            careType: this.careType,
            geoPointX: this.geoPoint.x,
            geoPointY: this.geoPoint.y,
            yearsExperience: parseInt(this.yearsExperience, 10),
          },
        })
        .then(({ data }) => {
          const dollarConversion = parseInt(data.sitterSearch?.averagePayRate, 10) / 100.0;
          const dollarRounded = Math.round(dollarConversion);
          this.averagePayRate = dollarRounded;
        })
        .catch((error) => {
          this.payRateFetchError = true;
          console.log(error.message); // eslint-disable-line no-console
        });
    },
  },
};
</script>
